import { safeLocalStorage } from '../utils/safeLocalStorage.js';

export class ApiError extends Error {
  statusCode: number;
  details?: any;

  constructor(message: string, statusCode: number, details?: any) {
    super(message);
    this.name = 'ApiError';
    this.statusCode = statusCode;
    this.details = details;
  }
}

export function getAuthToken(): string | null {
  try {
    const directToken = safeLocalStorage.getItem('uibn_token');
    if (directToken) return directToken;
    const raw = safeLocalStorage.getItem('uibn_auth_storage');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed.state?.token || parsed.token || null;
  } catch {
    return null;
  }
}

export function getEffectiveApiBaseUrl(): string {
  let envApi = '';
  try {
    if (typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_API_URL) {
      envApi = String((import.meta as any).env.VITE_API_URL).trim();
    }
  } catch {}

  if (envApi) return envApi.replace(/\/+$/, '');

  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    // When running on Cloudflare Pages (e.g. uibn-web-client.pages.dev) or custom domain, route API calls to backend custom domain or Cloudflare Worker
    if (host.includes('pages.dev') || host.includes('uibn.web.id')) {
      return 'https://api.uibn.web.id';
    }
  }
  return '';
}

export async function apiClient<T = any>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const token = getAuthToken();
  const headers = new Headers(options.headers || {});

  if (!headers.has('Content-Type') && !(options.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const apiBase = getEffectiveApiBaseUrl();
  const url = endpoint.startsWith('http') 
    ? endpoint 
    : `${apiBase}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;
  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    let errorData: any = {};
    try {
      errorData = await response.json();
    } catch {
      errorData = { error: response.statusText || 'Terjadi kesalahan pada server' };
    }

    // Auto-detect expired token on 401 Unauthorized
    if (response.status === 401 && token) {
      if (typeof window !== 'undefined') {
        window.dispatchEvent(
          new CustomEvent('uibn:session-expired', {
            detail: { message: errorData.error || 'Sesi Anda telah berakhir. Silakan masuk kembali.' },
          })
        );
      }
    }

    const message = errorData.error || errorData.message || `HTTP Error ${response.status}`;
    throw new ApiError(message, response.status, errorData.details);
  }

  // Handle blob responses (e.g. CSV or PDF download)
  const contentType = response.headers.get('content-type') || '';
  if (contentType.includes('text/csv') || contentType.includes('application/pdf')) {
    return (await response.blob()) as unknown as T;
  }

  // Handle empty or 204 responses
  if (response.status === 204) {
    return {} as T;
  }

  return response.json();
}
